import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  describeCall,
  formatDateTime,
  formatPhone,
  MESSAGE_STATUS_LABELS,
  type Call,
  type Message,
} from '@fsm/shared';
import { api } from '../lib/api';
import { Badge, Card, Spinner, cx } from './ui';

/**
 * Calls and texts, interleaved, newest first.
 *
 * One list rather than two: when a customer rings, gets voicemail, then texts,
 * the office needs to see that sequence, and two side-by-side panels hide it.
 */
export function CommunicationLog({
  customerId,
  jobId,
  limit = 20,
  title = 'Calls & texts',
}: {
  customerId?: string;
  jobId?: string;
  limit?: number;
  title?: string;
}) {
  const enabled = Boolean(customerId || jobId);
  const scope = { customerId, jobId, limit };

  const calls = useQuery({
    queryKey: ['calls', scope],
    queryFn: () => api.calls.list(scope as never),
    select: (d) => d.items,
    enabled,
  });

  const messages = useQuery({
    queryKey: ['messages', scope],
    queryFn: () => api.messages.list(scope as never),
    select: (d) => d.items,
    enabled,
  });

  const entries = useMemo(() => {
    const merged: Array<
      { at: number; kind: 'call'; call: Call } | { at: number; kind: 'message'; message: Message }
    > = [
      ...(calls.data ?? []).map((call) => ({
        at: new Date(call.startedAt).getTime(),
        kind: 'call' as const,
        call,
      })),
      ...(messages.data ?? []).map((message) => ({
        at: new Date(message.createdAt).getTime(),
        kind: 'message' as const,
        message,
      })),
    ];
    return merged.sort((a, b) => b.at - a.at).slice(0, limit);
  }, [calls.data, messages.data, limit]);

  const loading = calls.isLoading || messages.isLoading;

  return (
    <Card title={title} bodyClassName={entries.length > 0 ? 'p-0' : undefined}>
      {loading ? (
        <div className="grid place-items-center py-8">
          <Spinner className="size-5 text-ink-4" />
        </div>
      ) : entries.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-ink-4">Nothing yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {entries.map((entry) =>
            entry.kind === 'call' ? (
              <CallRow key={entry.call.id} call={entry.call} />
            ) : (
              <MessageRow key={entry.message.id} message={entry.message} />
            ),
          )}
        </ul>
      )}
    </Card>
  );
}

function CallRow({ call }: { call: Call }) {
  const inbound = call.direction === 'inbound';
  const bad = call.status === 'missed' || call.status === 'failed';

  return (
    <li className="flex gap-3 px-4 py-3">
      <span
        className={cx(
          'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-[13px]',
          bad ? 'bg-danger-soft text-danger' : 'bg-accent-soft text-accent',
        )}
        aria-hidden
      >
        {inbound ? '↙' : '↗'}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={cx('text-[13.5px] font-medium', bad && 'text-danger')}>
            {describeCall({
              direction: call.direction,
              status: call.status,
              durationSeconds: call.durationSeconds,
              handledByName: call.handledBy?.name ?? null,
            })}
          </span>
          {call.job && <Badge tone="blue">#{call.job.number}</Badge>}
        </div>
        <div className="text-[12px] tnum text-ink-4">
          {formatPhone(inbound ? call.fromNumber : call.toNumber)} ·{' '}
          {formatDateTime(call.startedAt)}
        </div>
        {call.notes && <p className="mt-1 text-[13px] text-ink-2">{call.notes}</p>}
        {call.recordingUrl && (
          <audio controls preload="none" src={call.recordingUrl} className="mt-2 h-8 w-full max-w-[320px]">
            <track kind="captions" />
          </audio>
        )}
        {call.transcript && (
          <details className="mt-1">
            <summary className="cursor-pointer text-[12px] text-accent">Transcript</summary>
            <p className="mt-1 text-[13px] whitespace-pre-wrap text-ink-2">{call.transcript}</p>
          </details>
        )}
      </div>
    </li>
  );
}

function MessageRow({ message }: { message: Message }) {
  const outbound = message.direction === 'outbound';
  const failed = message.status === 'failed';

  return (
    <li className="flex gap-3 px-4 py-3">
      <span
        className={cx(
          'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-[13px]',
          failed ? 'bg-danger-soft text-danger' : 'bg-good-soft text-good',
        )}
        aria-hidden
      >
        ✉
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[13.5px] font-medium">{outbound ? 'Sent' : 'Received'}</span>
          {failed ? (
            <Badge tone="red">Failed</Badge>
          ) : (
            <span className="text-[11.5px] text-ink-4">
              {MESSAGE_STATUS_LABELS[message.status]}
            </span>
          )}
          {message.job && <Badge tone="blue">#{message.job.number}</Badge>}
        </div>

        <p className="mt-0.5 text-[13.5px] whitespace-pre-wrap text-ink">{message.body}</p>

        {/* The template name belongs down here with the other metadata: in a
            sidebar column "· appointment reminder" in the heading wrapped onto
            its own line and pushed the delivery status out of view. */}
        <div className="mt-0.5 text-[12px] tnum text-ink-4">
          {formatPhone(outbound ? message.toNumber : message.fromNumber)} ·{' '}
          {formatDateTime(message.createdAt)}
          {message.sentBy && ` · ${message.sentBy.name}`}
          {message.template && ` · ${message.template.replace(/_/g, ' ')}`}
        </div>
        {failed && message.failureReason && (
          <p className="mt-1 text-[12.5px] text-danger">{message.failureReason}</p>
        )}
      </div>
    </li>
  );
}
